import { useState } from "react";
import {
  Text,
  Paper,
  Group,
  Stack,
  TextInput,
  Button,
  CopyButton,
  Alert,
  SegmentedControl,
  Select,
} from "@mantine/core";
import { createApiKey } from "../api";
import { useForm } from "@mantine/form";
import { useTranslation } from "react-i18next";

export function ApiShowKeyDialog({ newKey }) {
  const { t } = useTranslation();
  return (
    <Paper mt="md">
      <Stack>
        <Text>{t("tenant.api_key_new_intro", "Your new API key is:")}</Text>

        <Group>
          <TextInput
            style={{ flex: 1 }}
            readOnly
            value={newKey.tokenId}
          />

          <CopyButton value={newKey.tokenId}>
            {({ copied, copy }) => (
              <Button color={copied ? "teal" : "blue"} onClick={copy}>
                {copied
                  ? t("tenant.api_key_copied", "API key copied to clipboard")
                  : t("tenant.api_key_copy", "Copy API key")}
              </Button>
            )}
          </CopyButton>
        </Group>

        <Text mt="md" fw={600}>
          {t(
            "tenant.api_key_security_warning",
            "This key can't be shown again! Please copy this to a secure location. Do not share the key with anybody, it gives full access to your account!",
          )}
        </Text>
      </Stack>
    </Paper>
  );
}

export function ApiKeyCreateForm({ keyAdded, additionalData, brandName, brands }) {
  const [newKey, setNewKey] = useState(null);
  const { t } = useTranslation();

  const form = useForm({
    mode: "uncontrolled",
    initialValues: {
      name: "",
      isUserToken: false,
      permission: "readOnly",
      scope: additionalData?.brandId ? "brand" : "account",
      brandId: additionalData?.brandId || "",
    },
  });

  if (newKey) return <ApiShowKeyDialog newKey={newKey} />;

  const hasBrands = brands?.length > 0;
  const scopeValue = form.getValues().scope;

  return (
    <form
      onSubmit={form.onSubmit((values) => {
        const payload = {
          name: values.name,
          isUserToken: values.isUserToken,
          permission: values.permission,
        };
        if (values.scope === "brand" && values.brandId) {
          payload.brandId = values.brandId;
        }
        createApiKey(payload).then((data) => {
          setNewKey(data);
          keyAdded({ ...data, token: null, permission: values.permission.toLowerCase() });
        });
      })}
    >
      <Stack>
        {hasBrands && (
          <Stack gap="xs">
            <Text size="sm" fw={500}>{t("tenant.api_key_scope", "Scope")}</Text>
            <SegmentedControl
              data={[
                { label: t("tenant.api_key_scope_account", "All brands (account)"), value: "account" },
                { label: t("tenant.api_key_scope_brand", "Single brand"), value: "brand" },
              ]}
              {...form.getInputProps("scope")}
            />
          </Stack>
        )}

        {scopeValue === "brand" && hasBrands && (
          <Select
            label={t("tenant.api_key_brand_label", "Brand")}
            placeholder={t("tenant.api_key_brand_placeholder", "Select a brand")}
            data={brands.map((b) => ({ value: b.id, label: b.name }))}
            {...form.getInputProps("brandId")}
            required
          />
        )}

        {scopeValue === "account" && (
          <Alert variant="light" color="orange" title={t("tenant.api_key_account_alert_title", "Account-wide key")}>
            {t(
              "tenant.api_key_account_alert_msg",
              "This key will have access to all brands in your account, including any added in the future.",
            )}
          </Alert>
        )}

        {scopeValue === "brand" && form.getValues().brandId && (
          <Alert variant="light" color="blue" title={t("tenant.api_key_brand_alert_title", "Brand-scoped key")}>
            {t("tenant.api_key_brand_alert_msg", "This key will only have access to {{brandName}}.", {
              brandName: brands?.find((b) => b.id === form.getValues().brandId)?.name,
            })}
          </Alert>
        )}

        <TextInput
          my="sm"
          label={t("tenant.api_key_name_label", "Name")}
          description={t("tenant.api_key_name_desc", "An optional name for you to tell keys apart")}
          placeholder={t("tenant.api_key_name_placeholder", "My testing key")}
          {...form.getInputProps("name")}
          radius="md"
        />

        <Select
          label={t("tenant.api_key_permission_label", "Permission")}
          description={t(
            "tenant.api_key_permission_desc",
            "Read-only keys cannot create or modify data. Choose read & write for integrations that push data.",
          )}
          data={[
            { value: "readOnly", label: t("tenant.api_key_permission_readonly", "Read-only") },
            { value: "readWrite", label: t("tenant.api_key_permission_readwrite", "Read & write") },
            { value: "admin", label: t("tenant.api_key_permission_admin", "Admin (incl. tenant management)") },
          ]}
          allowDeselect={false}
          {...form.getInputProps("permission")}
          radius="md"
        />

        <Button type="submit">{t("tenant.api_key_create", "Create")}</Button>
      </Stack>
    </form>
  );
}
